import { useEffect, useLayoutEffect, useRef, useState, type ComponentPropsWithRef } from 'react';
import { Input } from './ui';

/**
 * A native date (or date-time) field that reports a value once it is chosen,
 * not every time the browser changes it on the way there.
 *
 * `<input type="date">` fires `input` -- React's `onChange` -- whenever its
 * value becomes a whole date, and that is far more often than "the user picked
 * one". Stepping the calendar popup to another month or year moves the value
 * to the same day in that month; typing a year fires on every digit, so
 * "2027" passes through 0002, 0020 and 0202 first. Wired straight to a save,
 * each of those was written to the server, and in the due-date popover the
 * first one closed the popover mid-pick.
 *
 * So the field keeps a draft and hands it over on blur, on Enter, or when it
 * unmounts with an edit outstanding -- the last because a popover or panel
 * can be dismissed by a click that never blurs the field first. Escape drops
 * the draft. A half-typed date (`validity.badInput`) is never committed: its
 * value reads as "", which would otherwise clear the date.
 */
export function DateInput({
  value,
  onCommit,
  type = 'date',
  onBlur,
  onKeyDown,
  ...props
}: Omit<ComponentPropsWithRef<'input'>, 'value' | 'onChange' | 'type'> & {
  compact?: boolean;
  /** `YYYY-MM-DD` for a date, `YYYY-MM-DDTHH:mm` for a date-time; "" for none. */
  value: string;
  type?: 'date' | 'datetime-local';
  /** Called only with a finished value that differs from `value`. */
  onCommit: (value: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const [shown, setShown] = useState(value);
  // A new value from outside -- a save landing, a failed one reverting --
  // replaces whatever draft there was.
  if (value !== shown) {
    setShown(value);
    setDraft(value);
  }

  // Read by the unmount commit, which only ever sees the first render's closure.
  const latest = useRef({ draft, value, onCommit, badInput: false });
  useLayoutEffect(() => {
    latest.current = { ...latest.current, draft, value, onCommit };
  });

  useEffect(
    () => () => {
      const { draft: pending, value: stored, onCommit: commit, badInput } = latest.current;
      if (!badInput && pending !== stored) commit(pending);
    },
    [],
  );

  function commit(field: HTMLInputElement) {
    if (field.validity.badInput) return;
    if (draft === value) return;
    // Settled as far as the unmount commit is concerned, so a popover that
    // closes in response does not get the same value a second time.
    latest.current.value = draft;
    onCommit(draft);
  }

  return (
    <Input
      {...props}
      type={type}
      value={draft}
      onChange={(event) => {
        latest.current.badInput = event.target.validity.badInput;
        setDraft(event.target.value);
      }}
      onBlur={(event) => {
        commit(event.currentTarget);
        onBlur?.(event);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          commit(event.currentTarget);
        } else if (event.key === 'Escape') {
          setDraft(value);
          latest.current.draft = value;
        }
        onKeyDown?.(event);
      }}
    />
  );
}
